import React from 'react';
import { Field, Form, Formik } from 'formik';
import { Button, Label } from 'edozvola-frontend-lib/dist/ui';
import { Col, FormGroup, Input, Row } from 'reactstrap';
import { useParams } from 'react-router-dom';
import { APP_DATE_FORMAT } from 'app/config/constants';
import { useDispatch } from 'react-redux';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { FormikDatepicker } from 'edozvola-frontend-lib/dist/ui/components/datepicker/FormikDatepicker';
import {
  fetchCaseFinalDocumentQuery,
  saveCaseFinalDocumentQuery,
} from 'app/edozvola/cases/case-final-document/case-final-document.queries';
import { Loader } from 'app/shared/loader/loader';
import { fetchResolutionDeadlineCodebookQuery, fetchResolutionTypeCodebookQuery } from 'app/edozvola/codebooks/codebooks.queries';
import { caseFinalDocumentSchema } from 'app/edozvola/cases/case-final-document/model/case-final-document.schema';
import FormikValueObserver from 'app/shared/formik-observer/formik-observer';
import {PaginatedDropdown} from "edozvola-frontend-lib/dist/ui/components/paginated-dropdown";

const CaseFinalDocumentForm = () => {
  const { id } = useParams<{ id: string }>();
  const { data: initialData, isLoading: initialDataLoading } = fetchCaseFinalDocumentQuery(id);
  const { data: resolutionTypeOptions, isLoading: resolutionTypeOptionsLoading } = fetchResolutionTypeCodebookQuery();
  const { data: resolutionDeadlineOptions, isLoading: resolutionDeadlineOptionsLoading } = fetchResolutionDeadlineCodebookQuery();
  const { mutate: saveCaseFinalDocumentMutation, isPending: isCaseFinalDocumentSaving } = saveCaseFinalDocumentQuery(id);
  const dispatch = useDispatch();

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={initialData}
        validationSchema={caseFinalDocumentSchema}
        validateOnMount={true}
        onSubmit={values => saveCaseFinalDocumentMutation(values)}
      >
        {({ errors, values, isValid, dirty, submitForm, setFieldValue }) => (
          <Form>
            <FormikValueObserver
              onChange={dirty => {
                if (dirty.dirty) {
                  dispatch(setFormChanged(true));
                } else {
                  dispatch(initCaseFormChange());
                }
              }}
            />
            {initialDataLoading && <Loader />}
            {!initialDataLoading && (
              <div>
                <h6 className={'form-header'}>Završni akt</h6>
                <Row>
                  <Col xs={4}>
                    <FormGroup>
                      <Label labelText={'Naziv završnog akta'} />
                      <Field
                        id="finalDocumentName"
                        name="finalDocumentName"
                        render={({ field }) => <Input {...field} type="input" disabled={true} error={errors.finalDocumentName} />}
                      />
                    </FormGroup>
                  </Col>
                  <Col xs={4}>
                    <FormGroup>
                      <Label labelText={'Urudžbeni broj'} />
                      <Field
                        id="finalDocumentFileNumber"
                        name="finalDocumentFileNumber"
                        render={({ field }) => <Input {...field} type="input" disabled={true} error={errors.finalDocumentFileNumber} />}
                      />
                    </FormGroup>
                  </Col>
                  <Col xs={4}>
                    <FormGroup>
                      <Label labelText={'Datum rješavanja'} />
                      <Field
                        id="finalDocumentAdmissionDate"
                        name="finalDocumentAdmissionDate"
                        render={({ field }) => (
                          <FormikDatepicker
                            field={field}
                            setFieldValue={setFieldValue}
                            dateFormat={APP_DATE_FORMAT}
                            disabled={true}
                            error={!values?.readonly && (errors.finalDocumentAdmissionDate as string)}
                          />
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col xs={4}>
                    <FormGroup>
                      <Label labelText={'Način rješavanja'} />
                      <Field
                        id="resolutionType"
                        name="resolutionType"
                        render={({ field }) => (
                          <PaginatedDropdown
                            onItemSelect={selectedOption => setFieldValue('resolutionType', selectedOption[0])}
                            selected={field.value ?? []}
                            labelKey={'value'}
                            options={resolutionTypeOptionsLoading ? [] : resolutionTypeOptions}
                            placeholder={!values?.readonly && 'Odaberi način rješavanja'}
                            disabled={values?.readonly}
                            error={!values?.readonly && (errors.resolutionType as string)}
                            showErrorMessage
                          />
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col xs={4}>
                    <FormGroup>
                      <Label labelText={'Ispunjenje roka'} />
                      <Field
                        id="resolutionDeadline"
                        name="resolutionDeadline"
                        render={({ field }) => (
                          <PaginatedDropdown
                            onItemSelect={selectedOption => setFieldValue('resolutionDeadline', selectedOption[0])}
                            selected={field.value ?? []}
                            labelKey={'value'}
                            options={resolutionDeadlineOptionsLoading ? [] : resolutionDeadlineOptions}
                            field={field}
                            setFieldValue={setFieldValue}
                            disabled
                            error={!values?.readonly && (errors.resolutionDeadline as string)}
                            showErrorMessage
                          />
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col xs={4}>
                    <FormGroup>
                      <Label labelText={'Obrazloženje neispunjenja roka'} />
                      <Field
                        id="resolutionDeadlineExplanation"
                        name="resolutionDeadlineExplanation"
                        render={({ field }) => (
                          <Input
                            {...field}
                            type="input"
                            error={errors.resolutionDeadlineExplanation}
                            disabled={values?.resolutionDeadline?.value !== 'Izvan roka'}
                          />
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <hr />
                <h6 className={'form-header'}>Oglasna ploča</h6>
                <Row>
                  <Col xs={4}>
                    <FormGroup>
                      <Label labelText={'Datum početka'} />
                      <Field
                        id="publishStartDate"
                        name="publishStartDate"
                        render={({ field }) => (
                          <FormikDatepicker field={field} setFieldValue={setFieldValue} dateFormat={APP_DATE_FORMAT} disabled={true} />
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col xs={4}>
                    <FormGroup>
                      <Label labelText={'Datum završetka'} />
                      <Field
                        id="publishEndDate"
                        name="publishEndDate"
                        render={({ field }) => (
                          <FormikDatepicker field={field} setFieldValue={setFieldValue} dateFormat={APP_DATE_FORMAT} disabled={true} />
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col xs={4}>
                    <FormGroup>
                      <Label labelText={'Obrazloženje ispunjenja roka'} />
                      <Field
                        id="resolutionDeadlineExplanation"
                        name="resolutionDeadlineExplanation"
                        render={({ field }) => (
                          <Input
                            {...field}
                            type="input"
                            error={errors.resolutionDeadlineExplanation}
                            disabled={!values?.resolutionDeadline || values?.resolutionDeadline[0]?.value !== 'Van roka'}
                          />
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {!values?.readonly && (
                  <div className="case-save-button">
                    <Button
                      color={'info'}
                      size={'lg'}
                      isLoading={isCaseFinalDocumentSaving}
                      label={'Spremi'}
                      onClick={submitForm}
                      disabled={!dirty || !isValid}
                    />
                  </div>
                )}
              </div>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
};

export default CaseFinalDocumentForm;
